import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, X, Wand2, Loader2, Link, FileText, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface JobAnalysisData {
  companyName: string;
  jobTitle: string;
  companyMission: string;
  responsibilities: string[];
  requirements: string[];
  skills: string[];
}

interface JobWizardStep1Props {
  onNext: (data: JobAnalysisData) => void;
  onClose: () => void;
}

export function JobWizardStep1({ onNext, onClose }: JobWizardStep1Props) {
  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState<'url' | 'text'>('url');
  const [jobUrl, setJobUrl] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isScraping, setIsScraping] = useState(false);

  const handleBackToDashboard = () => {
    onClose();
    navigate('/dashboard', { replace: true });
  };

  const handleScrapeUrl = async () => {
    if (!jobUrl.trim()) {
      toast.error("Please enter a job URL");
      return;
    }

    setIsScraping(true);
    try {
      const { data, error } = await supabase.functions.invoke('scrape-job-url', {
        body: { url: jobUrl.trim() }
      });

      if (error) throw error;

      if (data?.success && data?.jobContent) {
        setJobDescription(data.jobContent);
        toast.success("Job posting scraped successfully!");
      } else {
        throw new Error(data?.error || "Failed to scrape job posting");
      }
    } catch (error) {
      console.error('Scrape error:', error);
      toast.error("Failed to scrape job URL. Please paste the description manually.");
      setInputMode('text');
    } finally {
      setIsScraping(false);
    }
  };

  const handleAnalyze = async () => {
    if (!jobDescription.trim()) {
      toast.error("Please enter a job description");
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-job', {
        body: { jobContent: jobDescription }
      });

      if (error) throw error;

      // Support both response shapes:
      // 1) Direct analysis object { companyName, jobTitle, ... }
      // 2) Wrapped { success: true, analysis: { ... } }
      const payload = (data?.success && data?.analysis) ? data.analysis : data;

      if (payload && (payload.companyName || payload.jobTitle || Array.isArray(payload?.responsibilities))) {
        const analysisData: JobAnalysisData = {
          companyName: payload.companyName || "Unknown Company",
          jobTitle: payload.jobTitle || "Unknown Position",
          companyMission: payload.companyMission || "",
          responsibilities: payload.responsibilities || [],
          requirements: payload.requirements || [],
          skills: payload.skills || []
        };
        onNext(analysisData);
      } else {
        throw new Error("Analysis failed or returned invalid data");
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error("Failed to analyze job description. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-2">
      <div className="max-w-4xl mx-auto">

        <div className="bg-white rounded-lg shadow-lg">
          <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
            <div className="p-6 border-b border-border-light">
              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="outline" className="mb-2">Step 1 of 5</Badge>
                  <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                    <Wand2 className="w-5 h-5 text-primary" />
                    Job Description Analysis
                  </h1>
                  <p className="text-muted-foreground">
                    Provide a job URL or paste the description for AI analysis
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={handleBackToDashboard}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="mt-4">
                <Progress value={20} className="w-full" />
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Input Mode Toggle */}
            <div className="flex gap-2 mb-6">
              <Button
                variant={inputMode === 'url' ? 'default' : 'outline'}
                onClick={() => setInputMode('url')}
                className="flex-1"
              >
                <Link className="w-4 h-4 mr-2" />
                Job URL
              </Button>
              <Button
                variant={inputMode === 'text' ? 'default' : 'outline'}
                onClick={() => setInputMode('text')}
                className="flex-1"
              >
                <FileText className="w-4 h-4 mr-2" />
                Paste Description
              </Button>
            </div>

            {inputMode === 'url' && (
              <Card className="border-border-light shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Job Posting URL</CardTitle>
                  <CardDescription>
                    Paste the direct URL of the specific job posting—not a Google search results page or a page listing multiple jobs.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="https://www.linkedin.com/jobs/view/..."
                      value={jobUrl}
                      onChange={(e) => setJobUrl(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleScrapeUrl}
                      disabled={!jobUrl.trim() || isScraping}
                    >
                      {isScraping ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Scraping...
                        </>
                      ) : (
                        "Fetch Job"
                      )}
                    </Button>
                  </div>
                  {jobDescription && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 font-medium mb-2">✓ Job description fetched successfully</p>
                      <p className="text-sm text-green-600">{jobDescription.length} characters loaded</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {inputMode === 'text' && (
              <Card className="border-border-light shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Job Description</CardTitle>
                  <CardDescription>
                    Copy and paste the complete job description, including requirements, responsibilities, and company information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Paste the job description here..."
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    className="min-h-[300px] resize-none border-border-light focus:border-primary"
                  />
                  <div className="text-sm text-muted-foreground">
                    {jobDescription.length} characters
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between pt-6 border-t border-border-light mt-8">
              <Button variant="outline" onClick={handleBackToDashboard}>
                Cancel
              </Button>
              <Button 
                variant="gradient" 
                onClick={handleAnalyze}
                disabled={!jobDescription.trim() || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Analyze Job
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}